import { useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  setAstronautLatestAppliedProperties,
  setAstronautSelectedProperties,
} from '../../reducers/astronautPropertySelectSlice';
import { DATA_TOP_CHART_ID, DATA_MIDDLE_CHART_ID } from '../constants';
import {
  combineSignalsFromUrlToKeys,
  getConfigKeyForEntity,
  getSearchParams,
  updateMultipleSearchParams,
  validateDataExplorerEntityType,
} from '../utils';
import { useAppSelector } from '@/reducers/rootReducer';
import { entityInfoSelectors } from '@/reducers/entityInfoSlice';
import { dataConfigChartSelectors } from '@/reducers/dataConfigChartSlice';

const getValidNCaptureSignalKeys = (configChartsData): Set<string> => {
  const validKeys = new Set<string>();

  configChartsData?.forEach((category) => {
    category.values?.forEach((value) => {
      validKeys.add(value.dataLocationInStore);
    });
  });

  return validKeys;
};

const filterNCaptureActiveGraphs = (
  activeGraphs: Record<string, string>,
  validKeys: Set<string>,
) => {
  return Object.fromEntries(
    Object.entries(activeGraphs).filter(([key, value]) => {
      return (
        validKeys.has(key) &&
        (value === DATA_TOP_CHART_ID || value === DATA_MIDDLE_CHART_ID)
      );
    }),
  ) as Record<string, typeof DATA_TOP_CHART_ID | typeof DATA_MIDDLE_CHART_ID>;
};

const linkedSignalSuffixPattern = /\s\(([^)]+)\)$/;

const filterStaleLinkedActiveGraphs = (
  activeGraphs: Record<string, string>,
  linkedName?: string,
) => {
  const filteredActiveGraphs = Object.fromEntries(
    Object.entries(activeGraphs).filter(([key]) => {
      const linkedSignalSuffix = linkedSignalSuffixPattern.exec(key)?.[1];

      return !linkedSignalSuffix || linkedSignalSuffix === linkedName;
    }),
  ) as Record<string, typeof DATA_TOP_CHART_ID | typeof DATA_MIDDLE_CHART_ID>;

  return {
    activeGraphs: filteredActiveGraphs,
    hasChanges:
      Object.keys(filteredActiveGraphs).length !==
      Object.keys(activeGraphs).length,
  };
};

const updateSignalsUrl = (
  activeGraphs: Record<
    string,
    typeof DATA_TOP_CHART_ID | typeof DATA_MIDDLE_CHART_ID
  >,
) => {
  const topSignals: string[] = [];
  const middleSignals: string[] = [];

  Object.entries(activeGraphs).forEach(([key, value]) => {
    if (value === DATA_TOP_CHART_ID) {
      topSignals.push(key);
    }

    if (value === DATA_MIDDLE_CHART_ID) {
      middleSignals.push(key);
    }
  });

  updateMultipleSearchParams(
    [
      { key: DATA_TOP_CHART_ID, value: topSignals.join(',') },
      { key: DATA_MIDDLE_CHART_ID, value: middleSignals.join(',') },
    ],
    true,
  );
};

const useAstronautSignalsUrl = () => {
  const dispatch = useDispatch();
  const entityInfo = useAppSelector(entityInfoSelectors.selectEntityInfo);
  const dataChartConfig = useAppSelector(
    dataConfigChartSelectors.selectConfigChartsData,
  );
  const linkedDataChartConfig = useAppSelector(
    dataConfigChartSelectors.selectConfigLinkedChartsData,
  );

  useLayoutEffect(() => {
    const entityType = validateDataExplorerEntityType(entityInfo?.type);

    if (!entityType) {
      return;
    }

    const configKey = getConfigKeyForEntity(entityType);
    const urlSignals1 = getSearchParams(DATA_TOP_CHART_ID);
    const urlSignals2 = getSearchParams(DATA_MIDDLE_CHART_ID);

    const setProperties = (properties) => {
      dispatch(
        setAstronautSelectedProperties({
          entityType,
          properties,
        }),
      );
      dispatch(
        setAstronautLatestAppliedProperties({
          entityType,
          properties,
        }),
      );
    };

    if (urlSignals1 || urlSignals2) {
      const newKeys = combineSignalsFromUrlToKeys(urlSignals1, urlSignals2);
      const { activeGraphs: linkedSanitizedKeys, hasChanges } =
        filterStaleLinkedActiveGraphs(
          newKeys,
          linkedDataChartConfig?.linkedName,
        );

      if (hasChanges) {
        updateSignalsUrl(linkedSanitizedKeys);
      }

      if (entityType === 'NCapture') {
        const sanitizedKeys = filterNCaptureActiveGraphs(
          newKeys,
          getValidNCaptureSignalKeys(dataChartConfig),
        );
        setProperties(
          Object.keys(sanitizedKeys).length > 0 ? sanitizedKeys : null,
        );
        localStorage.updateObjectProperty(
          configKey,
          'activeGraphs',
          sanitizedKeys,
        );
      } else if (Object.entries(linkedSanitizedKeys).length > 0) {
        setProperties(linkedSanitizedKeys);
        localStorage.updateObjectProperty(
          configKey,
          'activeGraphs',
          linkedSanitizedKeys,
        );
      }
      return;
    }

    const activeGraphs = localStorage.getObject(configKey)?.['activeGraphs'];
    if (activeGraphs) {
      const { activeGraphs: linkedSanitizedActiveGraphs } =
        filterStaleLinkedActiveGraphs(
          activeGraphs,
          linkedDataChartConfig?.linkedName,
        );

      if (entityType === 'NCapture') {
        const sanitizedActiveGraphs = filterNCaptureActiveGraphs(
          activeGraphs,
          getValidNCaptureSignalKeys(dataChartConfig),
        );
        setProperties(
          Object.keys(sanitizedActiveGraphs).length > 0
            ? sanitizedActiveGraphs
            : null,
        );
        localStorage.updateObjectProperty(
          configKey,
          'activeGraphs',
          sanitizedActiveGraphs,
        );
      } else {
        setProperties(
          Object.keys(linkedSanitizedActiveGraphs).length > 0
            ? linkedSanitizedActiveGraphs
            : null,
        );
        localStorage.updateObjectProperty(
          configKey,
          'activeGraphs',
          linkedSanitizedActiveGraphs,
        );
      }
    }
  }, [entityInfo?.type, dataChartConfig, linkedDataChartConfig]);
};

export default useAstronautSignalsUrl;
